import type { ReactNode } from "react";
import { format, parseISO } from "date-fns";
import type {
  OfferAnalyticsGroupApi,
  OfferGroupSummaryResponse,
  RevenueTrendResponse,
  RoomUpgradeSummaryResponse,
  SurfaceTabResponse,
} from "@/types/analytics-offers";
import type { TenantOffersTableRow } from "@/types/analytics-tenant";
import { mapRoomUpgradeToKpis } from "@/components/pages/analytics/front-desk/components/offers/offersTabMappers";
import { buildOverviewMetricDelta } from "@/components/pages/analytics/front-desk/components/overview/overviewKpiMappers";
import type { OffersStage } from "@/components/pages/analytics/front-desk/components/offers/types";
import type { MetricCardTrend } from "@/components/pages/analytics/front-desk/components/shared/MetricCard";
import { formatAnalyticsCurrency } from "@/components/pages/analytics/front-desk/components/shared/analyticsFormatting";
import type {
  AdminHotelMetricRow,
  AdminMetricCell,
} from "../types/adminHotelMetricTable";

const GROUP_SECTION_IDS: Record<
  Exclude<OfferAnalyticsGroupApi, "ROOM_UPGRADE">,
  string
> = {
  UPSELL: "upsell",
  SERVICE: "service",
  ACTIVITY: "activities",
};

const GROUP_SECTION_TITLES: Record<
  Exclude<OfferAnalyticsGroupApi, "ROOM_UPGRADE">,
  string
> = {
  UPSELL: "Upsell Offers",
  SERVICE: "Service Offers",
  ACTIVITY: "Activities offers",
};

const HOTEL_CHART_COLORS = [
  "#CAC19F",
  "#B9B0FF",
  "#826BFF",
  "#6ECB9A",
  "#FF9B85",
  "#5BA4CF",
  "#E8A838",
  "#C47AC0",
] as const;

const STAGE_SECTION_ORDER: Partial<Record<OffersStage, readonly string[]>> = {
  dashboardCheckIn: ["upsell", "service", "activities"],
  dashboardStay: ["service", "activities"],
  dashboardCheckOut: ["upsell", "service", "activities"],
};

const DELTA_SUFFIX = " from previous period";

export type AdminOffersMetric = {
  title: string;
  value: string;
  icon?: ReactNode;
  delta?: string;
  trend?: MetricCardTrend;
};

export type AdminOffersSectionData = {
  id: string;
  title: string;
  metrics: AdminOffersMetric[];
  hotelRows: AdminHotelMetricRow[];
};

export type AdminOffersRevenueTrendHotel = {
  hotelId: string;
  hotelName: string;
  color: string;
};

export type AdminOffersRevenueTrendRow = {
  day: string;
  [hotelId: string]: string | number;
};

export type AdminOffersRevenueTrendData = {
  hotels: AdminOffersRevenueTrendHotel[];
  rows: AdminOffersRevenueTrendRow[];
};

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function formatNullablePercent(value: number | null): string {
  if (value == null) {
    return "—";
  }
  return `${value}%`;
}

function formatCentsAsCurrency(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }

  return formatAnalyticsCurrency(value / 100, {
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

function formatTrendDayLabel(dayKey: string): string {
  try {
    return format(parseISO(dayKey), "MMM d");
  } catch {
    return dayKey;
  }
}

function percentChange(current: number, previous: number): number {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function trendFromChange(changePct: number): MetricCardTrend {
  if (changePct === 0) {
    return "neutral";
  }
  return changePct > 0 ? "positive" : "negative";
}

function formatPercentDelta(changePct: number): {
  delta: string;
  trend: MetricCardTrend;
} {
  const sign = changePct > 0 ? "+" : "";
  const formatted = changePct.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });
  return {
    delta: `${sign}${formatted}%${DELTA_SUFFIX}`,
    trend: trendFromChange(changePct),
  };
}

function buildCountMetricCell(
  current: number,
  previous: number,
): AdminMetricCell {
  const { delta, trend } = formatPercentDelta(percentChange(current, previous));
  return {
    value: formatCount(current),
    delta,
    trend,
  };
}

function buildPercentMetricCell(
  current: number | null,
  previous: number | null,
): AdminMetricCell {
  const { delta, trend } = formatPercentDelta(
    percentChange(current ?? 0, previous ?? 0),
  );
  return {
    value: formatNullablePercent(current),
    delta,
    trend,
  };
}

function buildCurrencyMetricCell(
  currentCents: number | null | undefined,
  previousCents: number | null | undefined,
): AdminMetricCell {
  const { delta, trend } = formatPercentDelta(
    percentChange(currentCents ?? 0, previousCents ?? 0),
  );
  return {
    value: formatCentsAsCurrency(currentCents),
    delta,
    trend,
  };
}

function findGroupInSurface(
  surface: SurfaceTabResponse | null | undefined,
  offerGroup: OfferAnalyticsGroupApi,
): OfferGroupSummaryResponse | undefined {
  return surface?.groups.find((group) => group.offerGroup === offerGroup);
}

function getNonUpgradeGroupDefinitions(
  hotels: TenantOffersTableRow[],
  surfaceByHotelId: Map<string, SurfaceTabResponse | null>,
): Array<{
  id: string;
  title: string;
  offerGroup: Exclude<OfferAnalyticsGroupApi, "ROOM_UPGRADE">;
}> {
  for (const hotel of hotels) {
    const surface = surfaceByHotelId.get(hotel.hotelId);
    if (!surface?.groups.length) {
      continue;
    }

    return surface.groups
      .filter(
        (group): group is OfferGroupSummaryResponse =>
          group.offerGroup !== "ROOM_UPGRADE",
      )
      .map((group) => {
        const offerGroup = group.offerGroup as Exclude<
          OfferAnalyticsGroupApi,
          "ROOM_UPGRADE"
        >;
        return {
          id: GROUP_SECTION_IDS[offerGroup],
          title: GROUP_SECTION_TITLES[offerGroup],
          offerGroup,
        };
      });
  }

  return [];
}

function orderSectionsByIds<T extends { id: string }>(
  sections: T[],
  orderedIds: readonly string[],
): T[] {
  return orderedIds
    .map((id) => sections.find((section) => section.id === id))
    .filter((section): section is T => section != null);
}

function applyStageSectionOrder(
  sections: AdminOffersSectionData[],
  stage: OffersStage,
): AdminOffersSectionData[] {
  const orderedIds = STAGE_SECTION_ORDER[stage];
  if (!orderedIds) {
    return sections;
  }

  return orderSectionsByIds(sections, orderedIds);
}

function sumGroupTotals(
  hotels: TenantOffersTableRow[],
  surfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  offerGroup: OfferAnalyticsGroupApi,
): { contacted: number; onlyOpened: number; offered: number; skipped: number } {
  let contacted = 0;
  let onlyOpened = 0;
  let offered = 0;
  let skipped = 0;

  for (const hotel of hotels) {
    const group = findGroupInSurface(
      surfaceByHotelId.get(hotel.hotelId),
      offerGroup,
    );
    if (!group) {
      continue;
    }

    contacted += group.contacted;
    onlyOpened += group.onlyOpened;
    offered += group.offered;
    skipped += group.skipped;
  }

  return { contacted, onlyOpened, offered, skipped };
}

export function mapRoomUpgradeToKpisWithComparison(
  current: RoomUpgradeSummaryResponse | null | undefined,
  previous: RoomUpgradeSummaryResponse | null | undefined,
): AdminOffersMetric[] {
  const currentKpis = mapRoomUpgradeToKpis(current ?? undefined);
  const comparisons = [
    {
      current: current?.totalRevenueCents ?? 0,
      previous: previous?.totalRevenueCents ?? 0,
    },
    {
      current: current?.conversionRate ?? 0,
      previous: previous?.conversionRate ?? 0,
    },
    {
      current: current?.guestsOffered ?? 0,
      previous: previous?.guestsOffered ?? 0,
    },
    {
      current: current?.avgRevenueCents ?? 0,
      previous: previous?.avgRevenueCents ?? 0,
    },
  ];

  return currentKpis.map((metric, index) => ({
    ...metric,
    ...buildOverviewMetricDelta(
      comparisons[index].current,
      comparisons[index].previous,
    ),
  }));
}

export function mapRoomUpgradeHotelRows(
  hotels: TenantOffersTableRow[],
  currentSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  previousSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  hotelLogosById: Record<string, string | null>,
): AdminHotelMetricRow[] {
  return hotels.map((hotel) => {
    const currentUpgrade = currentSurfaceByHotelId.get(
      hotel.hotelId,
    )?.roomUpgrade;
    const previousUpgrade = previousSurfaceByHotelId.get(
      hotel.hotelId,
    )?.roomUpgrade;

    return {
      id: hotel.hotelId,
      hotelBrand: hotel.hotelName,
      hotelLogoId: hotelLogosById[hotel.hotelId] ?? null,
      metrics: {
        totalRevenue: buildCurrencyMetricCell(
          currentUpgrade?.totalRevenueCents ?? 0,
          previousUpgrade?.totalRevenueCents ?? 0,
        ),
        conversionRate: buildPercentMetricCell(
          currentUpgrade?.conversionRate ?? null,
          previousUpgrade?.conversionRate ?? null,
        ),
        guestsOffered: buildCountMetricCell(
          currentUpgrade?.guestsOffered ?? 0,
          previousUpgrade?.guestsOffered ?? 0,
        ),
        avgRevenue: buildCurrencyMetricCell(
          currentUpgrade?.avgRevenueCents,
          previousUpgrade?.avgRevenueCents,
        ),
      },
    };
  });
}

function aggregateGroupMetrics(
  hotels: TenantOffersTableRow[],
  currentSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  previousSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  offerGroup: OfferAnalyticsGroupApi,
): AdminOffersMetric[] {
  const currentTotals = sumGroupTotals(
    hotels,
    currentSurfaceByHotelId,
    offerGroup,
  );
  const previousTotals = sumGroupTotals(
    hotels,
    previousSurfaceByHotelId,
    offerGroup,
  );

  const metrics = [
    {
      title: "Total guests contacted",
      current: currentTotals.contacted,
      previous: previousTotals.contacted,
      format: formatCount,
    },
    {
      title: "Only Opened",
      current: currentTotals.onlyOpened,
      previous: previousTotals.onlyOpened,
      format: formatCount,
    },
    {
      title: "Guests offered",
      current: currentTotals.offered,
      previous: previousTotals.offered,
      format: formatCount,
    },
    {
      title: "Skipped",
      current: currentTotals.skipped,
      previous: previousTotals.skipped,
      format: formatCount,
    },
  ];

  return metrics.map((metric) => ({
    title: metric.title,
    value: metric.format(metric.current),
    ...buildOverviewMetricDelta(metric.current, metric.previous),
  }));
}

function mapGroupHotelRows(
  hotels: TenantOffersTableRow[],
  currentSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  previousSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  offerGroup: OfferAnalyticsGroupApi,
  hotelLogosById: Record<string, string | null>,
): AdminHotelMetricRow[] {
  return hotels.map((hotel) => {
    const currentGroup = findGroupInSurface(
      currentSurfaceByHotelId.get(hotel.hotelId),
      offerGroup,
    );
    const previousGroup = findGroupInSurface(
      previousSurfaceByHotelId.get(hotel.hotelId),
      offerGroup,
    );

    return {
      id: hotel.hotelId,
      hotelBrand: hotel.hotelName,
      hotelLogoId: hotelLogosById[hotel.hotelId] ?? null,
      metrics: {
        totalGuestsContacted: buildCountMetricCell(
          currentGroup?.contacted ?? 0,
          previousGroup?.contacted ?? 0,
        ),
        onlyOpened: buildCountMetricCell(
          currentGroup?.onlyOpened ?? 0,
          previousGroup?.onlyOpened ?? 0,
        ),
        guestsOffered: buildCountMetricCell(
          currentGroup?.offered ?? 0,
          previousGroup?.offered ?? 0,
        ),
        skipped: buildCountMetricCell(
          currentGroup?.skipped ?? 0,
          previousGroup?.skipped ?? 0,
        ),
      },
    };
  });
}

export function buildAdminOffersGroupSections(
  hotels: TenantOffersTableRow[],
  currentSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  previousSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  hotelLogosById: Record<string, string | null>,
  stage: OffersStage,
): AdminOffersSectionData[] {
  const definitions = getNonUpgradeGroupDefinitions(
    hotels,
    currentSurfaceByHotelId,
  );

  const sections = definitions.map((definition) => ({
    id: definition.id,
    title: definition.title,
    metrics: aggregateGroupMetrics(
      hotels,
      currentSurfaceByHotelId,
      previousSurfaceByHotelId,
      definition.offerGroup,
    ),
    hotelRows: mapGroupHotelRows(
      hotels,
      currentSurfaceByHotelId,
      previousSurfaceByHotelId,
      definition.offerGroup,
      hotelLogosById,
    ),
  }));

  return applyStageSectionOrder(sections, stage);
}

export function buildAdminRoomUpgradeSection(
  hotels: TenantOffersTableRow[],
  currentSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  previousSurfaceByHotelId: Map<string, SurfaceTabResponse | null>,
  hotelLogosById: Record<string, string | null>,
  currentSummary: RoomUpgradeSummaryResponse | null,
  previousSummary: RoomUpgradeSummaryResponse | null,
): AdminOffersSectionData {
  return {
    id: "room-upgrade",
    title: "Room Upgrade Offer",
    metrics: mapRoomUpgradeToKpisWithComparison(
      currentSummary,
      previousSummary,
    ),
    hotelRows: mapRoomUpgradeHotelRows(
      hotels,
      currentSurfaceByHotelId,
      previousSurfaceByHotelId,
      hotelLogosById,
    ),
  };
}

export function mapPerHotelTrendsToChartData(
  hotels: TenantOffersTableRow[],
  trendsByHotelId: Map<string, RevenueTrendResponse | null>,
): AdminOffersRevenueTrendData {
  const daySet = new Set<string>();

  for (const hotel of hotels) {
    const trend = trendsByHotelId.get(hotel.hotelId);
    trend?.series.forEach((point) => daySet.add(point.day));
  }

  const chartHotels = hotels.map((hotel, index) => ({
    hotelId: hotel.hotelId,
    hotelName: hotel.hotelName,
    color: HOTEL_CHART_COLORS[index % HOTEL_CHART_COLORS.length],
  }));

  const rows = [...daySet].sort().map((day) => {
    const row: AdminOffersRevenueTrendRow = { day: formatTrendDayLabel(day) };

    for (const hotel of hotels) {
      const point = trendsByHotelId
        .get(hotel.hotelId)
        ?.series.find((item) => item.day === day);
      row[hotel.hotelId] = point ? point.revenueCents / 100 : 0;
    }

    return row;
  });

  return { hotels: chartHotels, rows };
}
